// docs/66 RM10, RM11: the words for what is coming and what just came — the rule strip's lines for
// every scheduled card with its countdown, the leader's card's reserve, and the banners when a
// call or a raising lands (or is broken). Nothing here decides anything: the sim declares the call
// and writes its line; this only says it.
import type { Effect, PendingCard, ReserveView, Snapshot } from '../sim/types';

const ROLE_WORDS: Record<string, [string, string]> = {
  Screener: ['shieldbearer', 'shieldbearers'], Skirmisher: ['skirmisher', 'skirmishers'], Javelin: ['javelin', 'javelins'],
  Shaman: ['shaman', 'shamans'], Beast: ['beast', 'beasts'],
};
const KIN_WORDS: Record<string, string> = {
  Goblin: 'goblin', Orc: 'orc', Bandit: 'bandit', Cultist: 'cultist', DarkElf: 'dark elf', Skeleton: 'skeleton', Zombie: 'zombie',
  Gnoll: 'gnoll', Lizardfolk: 'lizardfolk', Wolf: 'wolf', Spider: 'spider', Troll: 'troll', Ooze: 'ooze', Beast: 'beast',
  Dragon: 'red dragon', MountainTroll: 'mountain troll', Yeti: 'yeti', Sandworm: 'sandworm', SpiderQueen: 'spider queen',
  Basilisk: 'basilisk', BlackDragon: 'black dragon', GreatEagle: 'great eagle', Ent: 'ent', GrimGiant: 'grim giant',
  // docs/116
  Kraken: 'kraken', MawOfTheDeep: 'maw of the deep',
  // docs/109
  Vampire: 'vampire', Ghost: 'ghost', Mummy: 'mummy', Demon: 'demon', Golem: 'golem', Elemental: 'elemental', Djinn: 'djinn', Witch: 'witch', Mimic: 'mimic', Beholder: 'eye', Gorgon: 'gorgon', Minotaur: 'minotaur', Cyclops: 'cyclops', Drake: 'drake', Vermin: 'giant', WinterCourt: 'winter', Fey: 'fey', Duergar: 'duergar', Soldier: 'men-at-arms',
  // docs/117
  Tentacles: 'tentacle', SandTentacles: 'sand tentacle',
};

/** "3 goblin skirmishers", "1 wolf". */
export function groupWords(g: Pick<ReserveView, 'role' | 'count' | 'kin'>): string {
  const [one, many] = ROLE_WORDS[g.role] ?? [g.role.toLowerCase(), `${g.role.toLowerCase()}s`];
  const kin = g.kin ? KIN_WORDS[g.kin] ?? g.kin.toLowerCase() : '';
  const noun = g.count === 1 ? one : many;
  return `${g.count} ${kin && kin !== one ? `${kin} ` : ''}${noun}`;
}

/** When a scheduled card lands, from the planning board's pulse. */
export function when(card: Pick<PendingCard, 'firesAt'>, pulse: number): string {
  const left = card.firesAt - pulse;
  return left <= 0 ? 'at the end of this turn' : left === 1 ? 'at the end of next turn' : `in ${left + 1} turns`;
}

/**
 * The rule strip's line for a scheduled card (RM11: "the HUD's rule strip lists anything pending
 * with its countdown"). A call or a raising carries its own words ("Grukk calls for the reserve —
 * 3 goblin skirmishers arrive from the north road at the end of this pulse"); a beat its label
 * and line; the countdown is added when the words do not already say when.
 */
export function pendingLine(card: PendingCard, pulse: number, snap?: Pick<Snapshot, 'reserve'>): string {
  let text = card.name.replace(' — ', ': ');
  if (!text) {
    if (card.kind === 'Call') {
      const g = card.group !== undefined ? snap?.reserve?.[card.group] : undefined;
      text = g ? `The leader calls its reserve: ${groupWords(g)} arrive${g.count === 1 ? 's' : ''}${g.from ? ` from ${g.from}` : ''}` : 'The leader calls its reserve';
    } else text = 'Something is about to happen';
  }
  return /end of (this|next) (pulse|turn)|in \d+ (pulses|turns)/.test(text) ? text : `${text} (${when(card, pulse)})`;
}

/**
 * The leader's card (RM10, RM11): the reserve that can still come, raisings left, a second form
 * to come — and once the leader is down, that nobody more will.
 */
export function reserveLines(snap: Pick<Snapshot, 'reserve' | 'raisings' | 'secondForm' | 'units' | 'leaderUnit'>): string[] {
  const left = (snap.reserve ?? []).filter((g) => !g.called);
  const leader = snap.leaderUnit !== undefined ? snap.units.find((u) => u.unitId === snap.leaderUnit) : undefined;
  const broken = !!leader && (leader.downed || leader.onBoard === false || leader.hp <= 0);
  const out: string[] = [];
  if (left.length) {
    if (broken) out.push('The leader is down: no more reinforcements.');
    else out.push(`${left.length} group${left.length === 1 ? '' : 's'} in reserve: ${left.map(groupWords).join(', ')}. Only the leader can call them, aloud.`);
  } else if ((snap.reserve ?? []).length) out.push('No reserve left to call.');
  if (snap.raisings && !broken) out.push(`Can raise the fallen ${snap.raisings === 1 ? 'once more' : `${snap.raisings} more times`}.`);
  if (snap.secondForm && !broken) out.push('It has a second form.');
  return out;
}

/** The banner (and the speaker's line) for a call or raising as it lands, or is broken. */
export function callBanner(
  e: Extract<Effect, { kind: 'Called' | 'Raised' | 'CallBroken' | 'RaiseBroken' | 'CaptiveFreed' | 'Retreat' }>,
  name: (unit: number) => string,
  snap?: Pick<Snapshot, 'reserve'>,
): { text: string; eyebrow?: string; danger?: boolean; speaker?: number; line?: string } | undefined {
  switch (e.kind) {
    case 'Called': {
      const g = snap?.reserve?.[e.group];
      const n = e.units.length || g?.count || 0;
      const who = g ? groupWords({ ...g, count: n }) : `${n} more`;
      return { text: `${name(e.leader)} calls the reserve — ${who} arrive${n === 1 ? 's' : ''}${g?.from ? ` from ${g.from}` : ''}; ${n === 1 ? 'it acts' : 'they act'} from the next turn`, eyebrow: 'Reinforcements', speaker: e.leader, line: e.line };
    }
    case 'CallBroken': return { text: `${name(e.leader)} is down — nobody answers the call: breaking the leader stops the reinforcements`, eyebrow: 'The call goes unanswered', danger: true };
    case 'Raised': {
      const who = e.units.map(name);
      return { text: `${name(e.caster)} calls the fallen back — ${who.join(' and ')} rise${who.length === 1 ? 's' : ''} on half their health, acting from the next turn`, eyebrow: 'The dead rise', speaker: e.caster, line: 'Rise!' };
    }
    case 'RaiseBroken': return { text: `${name(e.caster)} is down — the fallen stay down`, eyebrow: 'The raising fails', danger: true };
    case 'CaptiveFreed': return undefined;
    // docs/118 PT14a: the leader sounds the retreat; out of the fog it is only heard
    case 'Retreat': {
      const n = e.units.length;
      const runs = n === 0 ? 'the foe breaks and runs' : `${n === 1 ? 'the last foe runs' : `${n} foes run`} for the edge of the field`;
      if (e.leader === undefined) return { text: `A voice in the dark sounds the retreat — ${runs}`, eyebrow: 'Retreat!' };
      return { text: `${name(e.leader)} sounds the retreat — ${runs}`, eyebrow: 'Retreat!', speaker: e.leader, line: e.line };
    }
  }
}
